"use client";

import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import "./DoctorPostsPanel.css";

import usePostsDoctor from "../../../../feed/hooks/usePostsDoctor";
import useDoctors from "../../../../authDoctor/hooks/useDoctors";
import PostCard from "../../../../feed/components/HomePage/PostCard/PostCard";
import useSavedItems from "../../../../authorization/useSavedItems";
import { resolveDoctorAvatar } from "../../../hooks/useDoctorInformation";
import nopost from "../../../../assets/images/nopost.png";

const formatTimeAgo = (d) => {
  if (!d) return "";
  const secs = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  const map = [
    ["y", 31536000],
    ["mo", 2592000],
    ["w", 604800],
    ["d", 86400],
    ["h", 3600],
    ["m", 60],
  ];
  for (const [k, v] of map) {
    const n = Math.floor(secs / v);
    if (n >= 1) return `${n}${k}`;
  }
  return "now";
};

const normId = (v) => {
  if (v == null) return null;
  let s = String(v).trim();
  if (!s) return null;
  if (s.startsWith("DR_") || s.startsWith("dr_")) s = s.slice(3);
  if (s.startsWith("PAT_") || s.startsWith("pat_")) s = s.slice(4);
  s = s.trim();
  if (!s || ["nan", "undefined", "null"].includes(s.toLowerCase())) return null;
  return s;
};

export default function DoctorPostsPanel({ doctor }) {
  const { t } = useTranslation();

  const doctorId = normId(
    doctor?.id ??
    doctor?._id ??
    doctor?.userId ??
    doctor?.doctorId ??
    doctor?.pgId ??
    doctor?.raw?.id ??
    doctor?.raw?._id ??
    doctor?.raw?.userId ??
    doctor?.raw?.pgId
  );

  const { doctors: doctorsList = [] } = useDoctors();

  // ✅ state complet
  const fullState = useSelector((state) => state);

  // ✅ currentUser trouvé partout
  const currentUser = useSelector((state) => {
    const candidates = [
      state?.auth?.user,
      state?.auth?.currentUser,
      state?.auth?.data,
      state?.patient?.user,
      state?.patient?.currentPatient,
      state?.patient?.data,
      state?.patient?.patient,
      state?.user?.user,
      state?.user?.currentUser,
      state?.user?.data,
      state?.doctor?.user, // 👈 add
    ];

    for (const c of candidates) {
      if (c && typeof c === "object" && (c.id || c._id || c.userId || c.uuid)) {
        return c;
      }
    }
    return null;
  });

  // ✅ viewer role (important)
  const viewerIsDoctor = useMemo(() => {
    if (fullState?.doctor?.user) return true;
    const role = String(currentUser?.role || currentUser?.type || "").toLowerCase();
    if (role.includes("doctor")) return true;
    if (currentUser?.doctorId != null) return true;
    return false;
  }, [fullState?.doctor?.user, currentUser]);

  // ✅ doctorsByPgId
  const doctorsByPgId = useMemo(() => {
    const map = {};

    for (const d of doctorsList || []) {
      const id = normId(d?.id ?? d?.doctorId ?? d?.userId ?? d?.pgId ?? d?._id);
      if (!id) continue;

      const docInfo = {
        id,
        firstName: d?.firstName || d?.prenom || "",
        lastName: d?.lastName || d?.nom || "",
        fullName:
          d?.fullName ||
          d?.name ||
          `${d?.firstName || d?.prenom || ""} ${d?.lastName || d?.nom || ""}`.trim(),
        avatar: d?.avatar || d?.avatarUrl || d?.profileImage || d?.photo || d?.image || null,
        role: "doctor",
      };

      const numKey = String(Number(id));
      map[numKey] = docInfo;
      map[String(id)] = docInfo;
    }

    if (doctor) {
      const id = normId(doctor?.id ?? doctor?._id ?? doctor?.userId ?? doctor?.pgId);
      if (id) {
        const docInfo = {
          id,
          firstName: doctor?.firstName || doctor?.prenom || "",
          lastName: doctor?.lastName || doctor?.nom || "",
          fullName:
            doctor?.fullName ||
            doctor?.name ||
            `${doctor?.firstName || ""} ${doctor?.lastName || ""}`.trim(),
          avatar: doctor?.avatar || doctor?.avatarUrl || doctor?.profileImage || doctor?.photo || null,
          role: "doctor",
        };
        const numKey = String(Number(id));
        map[numKey] = docInfo;
        map[String(id)] = docInfo;
      }
    }

    return map;
  }, [doctorsList, doctor]);

  // ✅ usersById avec currentUser
  const usersById = useMemo(() => {
    const map = {};
    if (currentUser) {
      const id = normId(
        currentUser?.id ??
        currentUser?._id ??
        currentUser?.userId ??
        currentUser?.uuid ??
        currentUser?.patientId
      );

      if (id) {
        map[String(id)] = {
          id,
          firstName: currentUser?.firstName || currentUser?.prenom || "",
          lastName: currentUser?.lastName || currentUser?.nom || "",
          fullName:
            currentUser?.fullName ||
            currentUser?.name ||
            `${currentUser?.firstName || currentUser?.prenom || ""} ${currentUser?.lastName || currentUser?.nom || ""}`.trim(),
          avatar:
            currentUser?.avatar ||
            currentUser?.avatarUrl ||
            currentUser?.profileImage ||
            currentUser?.photo ||
            currentUser?.image ||
            null,
          role: currentUser?.role || "patient",
        };
      }
    }
    return map;
  }, [currentUser]);

  const { posts, status, error } = usePostsDoctor(doctorId, {
    autoFetch: !!doctorId,
    pageSize: 10,
    mode: "user",
    page: 1,
  });
  const { isSaved, toggleSave, pendingKey } = useSavedItems({ enabled: viewerIsDoctor });

  // ✅ FILTRAGE FRONT: patient ne voit pas doctors-only
  const visiblePosts = useMemo(() => {
    const arr = Array.isArray(posts) ? posts : [];
    if (viewerIsDoctor) return arr;
    return arr.filter((p) => p?.visibleToDoctorsOnly !== true);
  }, [posts, viewerIsDoctor]);

  if (!doctor) return null;

  return (
    <div className="dp-card">
      {status === "loading" && visiblePosts.length === 0 && (
        <div className="dp-loading">{t("doctorPostsPanel.loading")}</div>
      )}

      {error && (
        <div className="dp-error">
          {t("doctorPostsPanel.error")}: {String(error)}
        </div>
      )}

      {status !== "loading" && visiblePosts.length === 0 ? (
        <div className="dp-empty-state">
          <img
            src={nopost}
            alt={t("doctorPostsPanel.noPosts")}
            className="dp-empty-image"
          />
        </div>
      ) : (
        <div className="dp-list">
          {visiblePosts.map((p) => {
            const pid = p.postId || p.id || p._id;
            return (
              <div key={pid} className="dp-item">
                <PostCard
                  postId={pid}
                  doctorName={
                    p.author?.fullName ||
                    p.author?.name ||
                    doctor?.fullName ||
                    doctor?.name ||
                    t("doctorPostsPanel.doctor")
                  }
                  timeAgo={formatTimeAgo(p.createdAt)}
                  content={p.content || p.text}
                  image={p.imageUrl}
                  attachments={p.attachments || []}
                  likes={p.likesCount ?? p.likes ?? 0}
                  comments={p.commentsCount ?? p.comments ?? 0}
                  profileImage={resolveDoctorAvatar(
                    p.author?.avatar ||
                    p.author?.avatarUrl ||
                    doctor?.avatarUrl ||
                    doctor?.avatar ||
                    doctor?.profileImage ||
                    doctor?.photo
                  )}
                  currentUserId={normId(currentUser?.id ?? currentUser?._id ?? currentUser?.userId)}
                  postOwnerId={String(doctorId ?? "")}
                  isSaved={isSaved("post", pid)}
                  onToggleSave={(postId) => toggleSave("post", postId)}
                  isSaving={pendingKey === `post:${String(pid)}`}
                  createdAt={p.createdAt}
                  doctorsByPgId={doctorsByPgId}
                  usersById={usersById}
                  visibleToDoctorsOnly={!!p.visibleToDoctorsOnly}  
                  onEdit={null}
                  onDelete={null}
                  isUpdating={false}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}